import {
  AGE_BRACKETS,
  DEPARTMENTS,
  RADIUS_ESTIMATES,
  RESIDENCE_OPTIONS,
  SECTORS,
  TOTAL_CITIZENS,
} from '../data/communications'
import type { AudienceState } from '../types/composer'

/** Total number of citizens reached by the current audience selection. */
export function getAudienceTotal(audience: AudienceState): number {
  const {
    zone,
    selectedSectors,
    selectedDepartments,
    selectedAges,
    residence,
    drawnPoints,
    addrCoords,
    locationRadius,
  } = audience

  if (zone === 'org') return TOTAL_CITIZENS

  if (zone === 'sector') {
    return SECTORS.filter((s) => selectedSectors.includes(s.id)).reduce(
      (sum, s) => sum + s.population,
      0,
    )
  }

  if (zone === 'filter') {
    // Base = subscribers of the selected departments (capped at the total
    // population), or the whole population when no department is selected.
    const base =
      selectedDepartments.length > 0
        ? Math.min(
            DEPARTMENTS.filter((d) => selectedDepartments.includes(d.id)).reduce(
              (sum, d) => sum + d.subscribers,
              0,
            ),
            TOTAL_CITIZENS,
          )
        : TOTAL_CITIZENS

    const ageFactor =
      selectedAges.length > 0
        ? AGE_BRACKETS.filter((a) => selectedAges.includes(a.id)).reduce(
            (sum, a) => sum + a.share,
            0,
          )
        : 1

    const residenceFactor =
      RESIDENCE_OPTIONS.find((r) => r.value === residence)?.share ?? 1

    return Math.round(base * ageFactor * residenceFactor)
  }

  // location
  if (drawnPoints.length >= 3) {
    // Believable estimate that scales with the polygon complexity.
    return Math.min(5000 + drawnPoints.length * 3200, 42000)
  }
  if (addrCoords) return RADIUS_ESTIMATES[locationRadius] ?? 0
  return 0
}

/** Per-channel reach derived from the targeted audience. */
export function getReach(
  audienceTotal: number,
  basePercent: number,
  locale: string,
) {
  const count = Math.round(audienceTotal * (basePercent / 100))
  return {
    hasAudience: audienceTotal > 0,
    count: count.toLocaleString(locale),
    percent: basePercent,
  }
}
